import gsap from 'gsap';
import 'vite/modulepreload-polyfill';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Swiper from 'swiper';
import { Autoplay, EffectFade, Keyboard, Navigation } from 'swiper/modules';
import { createApp, onMounted, onUnmounted, ref } from 'vue';

import * as AnalyticsData from '~/analytics/analytics-data';

import { BaseSection } from './base-section';

gsap.registerPlugin(ScrollTrigger);

const sectionName = 'global-banner';

/**
 * Global Banner Section
 *
 * This section displays a global banner carousel with a messages and links.
 *
 * @extends BaseSection
 */
class GlobalBanner extends BaseSection {
  constructor() {
    super(`[data-section-type="${sectionName}"]`);
  }

  init(instanceData) {
    const container = instanceData.container;

    createApp({
      name: 'GlobalBannerSection',
      setup() {
        const carousel = ref(null);
        const autoplayDelay = Number(container.getAttribute('data-autoplay-delay'));

        onMounted(() => {
          const swiperContainer = container.querySelector('.swiper');

          if (swiperContainer) {
            carousel.value = new Swiper(swiperContainer, {
              autoplay: {
                delay: autoplayDelay,
                disableOnInteraction: false,
              },

              effect: 'fade',
              fadeEffect: {
                crossFade: true,
              },
              keyboard: {
                enabled: true,
              },
              loop: true,
              modules: [Autoplay, EffectFade, Navigation, Keyboard],
              navigation: {
                nextEl: '.global-banner-next-button',
                prevEl: '.global-banner-prev-button',
              },
              slidesPerView: 1,
            });

            document.body.classList.add('global-banner-active');
          }

          // Hook the analytics events
          AnalyticsData.hookGlobalBannerLinkClicked({ sectionName });
        });

        onUnmounted(() => {
          if (carousel.value) {
            carousel.value.destroy();
            carousel.value = null;
          }

          document.body.classList.remove('global-banner-active');
          document.body.classList.remove('global-banner-in-view');
        });

        return {};
      },
    }).mount(container);
  }

  setupInViewAnimationTimeline(instanceData) {
    const container = instanceData.container;
    if (!container) return null;

    const tl = gsap.timeline({
      paused: true,
      scrollTrigger: {
        once: false,
        onEnter: () => {
          if (document.body.style.position === 'fixed') return;

          document.body.classList.add('global-banner-in-view');
        },
        onEnterBack: () => {
          if (document.body.style.position === 'fixed') return;

          document.body.classList.add('global-banner-in-view');
        },
        onLeave: () => {
          if (document.body.style.position === 'fixed') return;

          document.body.classList.remove('global-banner-in-view');
        },
        trigger: container,
      },
    });

    if (!tl) return null;

    return tl;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new GlobalBanner();
});
